<template>
  <section class="batch-modify purchase-limit">
    <header class="batch-head">
      <div class="crumb">
        <button class="plain" :disabled="locked" @click="goBack">‹ 返回</button>
        <span>
          商品工作台　›　批量修改　›　
          <strong>商品限购</strong>
        </span>
      </div>
      <h1>批量修改商品限购</h1>
      <nav>
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="{ active: mode === tab.value }"
          :disabled="locked"
          @click="switchMode(tab.value)">
          {{ tab.label }}
        </button>
      </nav>
    </header>
    <main>
      <p v-if="optionsError" class="load-error">
        {{ optionsError }}
        <button class="plain" :disabled="optionsLoading" @click="loadOptions">重新加载</button>
      </p>
      <p v-else-if="!optionsLoading && !shopOptions.length" class="load-error">
        暂无可操作的授权抖店，请先完成店铺授权
      </p>
      <section class="panel scope-panel">
        <div v-if="mode === 'products'" class="picked">
          <button :disabled="locked || !canQuery" @click="openSelector">选择商品</button>
          <span>
            已选择
            <b>{{ selected.length }}</b>
            个商品
          </span>
        </div>
        <template v-else>
          <div v-if="mode === 'product_ids'" class="id-area">
            <label for="purchase-product-ids">抖店商品 ID</label>
            <el-input
              id="purchase-product-ids"
              v-model="idsText"
              :disabled="locked"
              type="textarea"
              :rows="3"
              placeholder="多个 ID 用逗号、空格或换行分隔，最多2000个" />
            <label class="draft-control">
              是否草稿
              <el-select v-model="idDraft" :disabled="locked">
                <el-option label="全部" value="" />
                <el-option label="是" :value="1" />
                <el-option label="否" :value="0" />
              </el-select>
            </label>
          </div>
          <div v-else class="filters">
            <label v-for="field in shownFields" :key="field.key" class="field">
              <span>{{ field.label }}</span>
              <doudian-category-cascader
                v-if="field.key === 'category_id'"
                v-model="filters.category_id"
                :categories="categories"
                :disabled="locked" />
              <el-date-picker
                v-else-if="field.date"
                v-model="filters[field.key]"
                :disabled="locked"
                type="datetimerange"
                value-format="yyyy-MM-dd HH:mm:ss"
                start-placeholder="开始时间"
                end-placeholder="结束时间" />
              <div v-else-if="field.matchKey" class="combined-control">
                <el-select v-model="filters[field.matchKey]" :disabled="locked">
                  <el-option v-for="o in field.matchOptions" :key="o.value" :label="o.label" :value="o.value" />
                </el-select>
                <div v-if="field.range" class="range">
                  <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" />
                  <span>~</span>
                  <input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
                </div>
                <input
                  v-else
                  v-model="filters[field.key]"
                  :disabled="locked || (field.key === 'sku_code' && filters.sku_code_mode === 'empty')"
                  :placeholder="field.placeholder || '请输入' + field.label" />
              </div>
              <div v-else-if="field.range" class="range">
                <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" />
                <span>~</span>
                <input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
              </div>
              <el-select v-else-if="field.options" v-model="filters[field.key]" :disabled="locked" clearable>
                <el-option v-for="o in field.options" :key="String(o.value)" :label="o.label" :value="o.value" />
              </el-select>
              <input
                v-else
                v-model="filters[field.key]"
                :disabled="locked"
                :placeholder="field.placeholder || '请输入条件'" />
            </label>
          </div>
          <footer class="filter-actions">
            <doudian-batch-shop-scope v-model="shops" :shops="shopOptions" :disabled="locked" />
            <div class="filter-buttons">
              <button :disabled="!canQuery || previewLoading || locked" @click="loadPreview(1, false)">查询</button>
              <button class="plain" :disabled="locked" @click="resetFilters">重置</button>
            </div>
          </footer>
        </template>
        <p class="hint">
          先按本地商品数据筛选，执行时读取抖店最新详情复核。未选择店铺时仅查询当前账号有权限的授权抖店。
        </p>
      </section>
      <section class="panel limit-rules">
        <h3>
          <i></i>
          修改内容
        </h3>
        <div class="purchase-limit-editor">
          <span class="editor-label">商品限购</span>
          <div class="limit-rule-content">
            <div class="limit-rule-fields">
              <div v-for="item in editorFields" :key="item.key" class="limit-rule-row">
                <label :for="'limit-' + item.key">
                  {{ item.editorLabel }}
                  <el-tooltip :content="item.tip" placement="top">
                    <i class="el-icon-question" :aria-label="item.editorLabel + '说明'" tabindex="0"></i>
                  </el-tooltip>
                </label>
                <el-select
                  v-model="rule[item.key].action"
                  class="rule-action"
                  :aria-label="item.label + '修改方式'"
                  :disabled="locked"
                  size="mini">
                  <el-option label="不修改" value="keep" />
                  <el-option label="设置数量" value="set" />
                  <el-option label="清空" value="clear" />
                </el-select>
                <el-input
                  :id="'limit-' + item.key"
                  class="quantity"
                  :value="rule[item.key].action === 'set' ? rule[item.key].value : ''"
                  :aria-label="item.label + '件数'"
                  inputmode="numeric"
                  :disabled="locked || rule[item.key].action !== 'set'"
                  :placeholder="item.key === 'maximum_per_order' ? '未设置时按类目推荐' : '请输入'"
                  @input="rule[item.key].value = $event"
                  @blur="validateField(item.key)">
                  <span slot="suffix" class="quantity-unit">件</span>
                </el-input>
                <small v-if="ruleErrors[item.key]" class="rule-error">{{ ruleErrors[item.key] }}</small>
                <small v-else-if="item.key === 'minimum_per_order' && rule[item.key].action === 'clear'" class="hint">
                  清空后恢复1件起购
                </small>
              </div>
            </div>
            <p class="limit-activity-hint">若商品参加活动限购，请在抖店商品管理中查看活动规则。</p>
          </div>
        </div>
        <p v-if="combinationError" class="rule-error" role="alert">{{ combinationError }}</p>
        <p class="hint">
          每次限购件数不能超过累计限购件数，未设置“每次限购”时，每次最大购买件数为该类目的系统推荐限购件数。起购数不能超过有效的购买上限，实际范围以平台类目规则为准。
        </p>
      </section>
      <section v-if="preview" class="panel preview limit-preview" :aria-busy="previewLoading">
        <header class="preview-summary">
          <span>
            候选
            <b>{{ preview.total }}</b>
            个，已排除 {{ preview.excluded_total }} 个，本次提交 {{ preview.selected_total }} 个；本页预计命中
            {{ counts.ready }}、预计跳过 {{ counts.skipped }}、待核验 {{ counts.unknown }}、预计失败 {{ counts.failed }}
          </span>
          <button class="plain" :disabled="locked || previewLoading" @click="closePreview">收起预览</button>
        </header>
        <p class="hint">预览基于本地快照，实际命中和修改结果以执行时抖店详情复核为准。</p>
        <p v-if="!previewCurrent" class="preview-warning" role="status">条件已变化，请重新预览。</p>
        <p v-if="previewError" class="load-error" role="alert">{{ previewError }}</p>
        <p v-if="preview.total > 10000" class="load-error">候选商品超过10000个，请缩小范围</p>
        <details v-if="(preview.missing_product_ids || []).length">
          <summary>当前授权范围内未找到 {{ preview.missing_product_ids.length }} 个商品 ID</summary>
          <p class="missing-ids">{{ preview.missing_product_ids.join('、') }}</p>
        </details>
        <div class="table-wrap">
          <table class="limit-preview-table">
            <thead>
              <tr>
                <th>商品信息</th>
                <th>修改前</th>
                <th>计划修改后</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in preview.list" :key="row.id" :class="{ 'excluded-row': row.is_excluded }">
                <td>
                  <div class="preview-product">
                    <el-image class="preview-product-image" :src="row.main_image" fit="cover">
                      <span slot="error">暂无图片</span>
                    </el-image>
                    <div>
                      <small>{{ row.shop_name }}</small>
                      <p :title="row.product_title">{{ row.product_title }}</p>
                      <small>商品 ID：{{ row.platform_product_id }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <div v-for="field in ruleFields" :key="field.key" class="value-line">
                    {{ field.short }}：{{ oldLabel(row.old_values && row.old_values[field.key], field.key) }}
                  </div>
                </td>
                <td>
                  <small v-if="['skipped', 'failed'].includes(row.preview_result)" class="not-executing">不执行</small>
                  <div v-for="field in ruleFields" :key="field.key" class="value-line">
                    {{ field.short }}：{{ targetLabel(row, field.key) }}
                  </div>
                </td>
                <td>
                  <span class="match-badge" :class="'result-' + row.preview_result">
                    {{ resultLabels[row.preview_result] || '待核验' }}
                  </span>
                  <p class="preview-reason">{{ row.reason_text || '暂无法确定，请执行时核验' }}</p>
                </td>
                <td>
                  <el-checkbox
                    :value="excluded.includes(Number(row.id))"
                    :disabled="locked || previewLoading"
                    @change="exclude(row.id, $event)">
                    排除修改
                  </el-checkbox>
                </td>
              </tr>
              <tr v-if="!preview.list.length"><td colspan="5" class="empty">暂无符合条件的商品，请调整筛选条件</td></tr>
            </tbody>
          </table>
          <div v-if="previewLoading" class="preview-cover" role="status">预览加载中…</div>
        </div>
        <footer class="preview-pagination">
          <el-pagination
            small
            layout="prev, pager, next"
            :disabled="previewLoading || locked"
            :current-page="preview.page"
            :page-size="20"
            :total="preview.total"
            @current-change="loadPreview($event, false)" />
        </footer>
      </section>
    </main>
    <footer class="actions limit-actions">
      <button class="preview-button" :disabled="!canQuery || previewLoading || locked" @click="loadPreview(1, true)">
        {{ previewLoading ? '加载中…' : '修改内容预览' }}
      </button>
      <button v-if="createUncertain" class="start" :disabled="createSubmitting" @click="submitFrozen">
        {{ createSubmitting ? '正在确认…' : '确认创建结果' }}
      </button>
      <button v-else class="start" :disabled="!canCreate" @click="createBatch">
        {{ createSubmitting ? '正在创建…' : '开始批量修改' }}
      </button>
      <label>
        <input
          :checked="scheduled"
          :disabled="locked"
          type="checkbox"
          @change="toggleSchedule($event.target.checked)" />
        定时执行
      </label>
      <button v-if="scheduled" class="time" :disabled="locked" @click="openSchedule">
        {{ scheduledAt || '选择时间' }}
      </button>
      <small v-if="createUncertain" class="load-error">创建结果尚未确认，请确认创建结果，勿重复新建</small>
    </footer>
    <el-dialog
      title="定时执行"
      :visible.sync="scheduleVisible"
      width="min(560px, 92vw)"
      append-to-body
      @closed="scheduleClosed">
      <p>请选择北京时间</p>
      <el-date-picker
        v-model="scheduleDraft"
        type="datetime"
        format="yyyy-MM-dd HH:mm"
        value-format="yyyy-MM-dd HH:mm:ss"
        placeholder="选择未来的执行时间" />
      <span slot="footer">
        <el-button @click="scheduleVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSchedule">确定</el-button>
      </span>
    </el-dialog>
    <product-selector-dialog
      @page-size-change="changeSelectorPageSize"
      :visible="selectorVisible"
      :active-tab="selectorTab"
      :products="selectorProducts"
      :selected-ids="draftSelected.map(productId)"
      :loading="selectorLoading"
      :page="selectorPage"
      :page-size="selectorPageSize"
      :page-sizes="[20, 50, 100, 500, 1000]"
      :total="selectorTab === 'selected' ? draftSelected.length : selectorTotal"
      :total-pages="selectorPages"
      :jump-page="selectorJump"
      :page-fully-selected="selectorRows.length > 0 && selectorRows.every(isSelected)"
      :product-id="productId"
      :product-key="productId"
      :product-title="productTitle"
      :product-image="productImage"
      :product-price="productPrice"
      @tab-change="changeSelectorTab"
      @close="selectorVisible = false"
      @toggle-product="toggleProduct"
      @toggle-page="togglePage"
      @page-change="changeSelectorPage"
      @jump-input="selectorJump = $event"
      @jump="changeSelectorPage(Number(selectorJump))"
      @confirm="confirmSelected">
      <template #filters>
        <doudian-product-selector-filters
          :value="selectorFilters"
          :shop-id="selectorShopId"
          :shops="shopOptions"
          :categories="categories"
          @shop-change="changeSelectorShop"
          @query="loadSelector(1)" />
      </template>
    </product-selector-dialog>
  </section>
</template>

<script>
import DoudianBatchShopScope from '@/components/DoudianBatchShopScope'
import DoudianCategoryCascader from '@/components/DoudianCategoryCascader'
import ProductSelectorDialog from '@/components/ProductSelectorDialog/ProductSelectorDialog'
import DoudianProductSelectorFilters from './DoudianProductSelectorFilters.vue'
import { fields, pairedFields, defaults, buildBatchProductFilter } from './batchModifyFilters'
import {
  previewDoudianPurchaseLimitBatch,
  createDoudianPurchaseLimitBatch,
  getAuthSuccessShopList,
  getDoudianCategories,
  getProductSelectorProducts
} from '@/api/main/doudianBatchEdit'

const ruleFields = [
  { key: 'limit_per_buyer', label: '累计限购', short: '累计' },
  { key: 'maximum_per_order', label: '每次限购', short: '每次' },
  { key: 'minimum_per_order', label: '每次至少购买', short: '起购' }
]
const allowedFields = [
  'category_id',
  'status',
  'title_mode',
  'product_title',
  'platform_product_id',
  'stock',
  'price_mode',
  'price',
  'created_at',
  'brand_id',
  'presell',
  'freight_id',
  'is_distribute',
  'product_no_mode',
  'product_no',
  'sales',
  'is_draft',
  'sku_code_mode',
  'sku_code',
  'sku_name_mode',
  'sku_name',
  'fabric_material',
  'has_video'
]
const makeRule = () => Object.fromEntries(ruleFields.map((f) => [f.key, { action: 'keep', value: '' }]))
const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 3) | 8).toString(16)
  })

export default {
  name: 'BatchModifyPurchaseLimit',
  components: { DoudianBatchShopScope, DoudianCategoryCascader, ProductSelectorDialog, DoudianProductSelectorFilters },
  data: () => ({
    tabs: [
      { value: 'range', label: '按范围修改' },
      { value: 'products', label: '按商品修改' },
      { value: 'product_ids', label: '按商品 ID 修改' }
    ],
    mode: 'range',
    filters: defaults(),
    shops: [],
    selected: [],
    excluded: [],
    idsText: '',
    idDraft: '',
    shopOptions: [],
    categories: [],
    optionsLoading: true,
    optionsError: '',
    ruleFields,
    rule: makeRule(),
    ruleErrors: {},
    combinationError: '',
    preview: null,
    previewSignature: '',
    previewRequest: 0,
    previewLoading: false,
    previewError: '',
    createSubmitting: false,
    createUncertain: false,
    requestId: uuid(),
    frozenCreatePayload: null,
    scheduled: false,
    scheduledAt: '',
    scheduleDraft: '',
    scheduleVisible: false,
    selectorVisible: false,
    selectorTab: 'all',
    selectorLoading: false,
    selectorRows: [],
    selectorPageSize: 20,
    selectorPage: 1,
    selectorTotal: 0,
    selectorJump: 1,
    selectorShopId: '',
    selectorFilters: {
      category_id: { level1: [], level2: [], level3: [] },
      platform_product_id: '',
      product_title: '',
      created_at: [],
      tab_status: ''
    },
    draftSelected: [],
    selectorRequest: 0,
    resultLabels: { ready: '预计命中', skipped: '预计跳过', unknown: '待核验', failed: '预计失败' }
  }),
  computed: {
    locked() {
      return this.createSubmitting || this.createUncertain
    },
    canQuery() {
      return !this.optionsLoading && !this.optionsError && this.shopOptions.length > 0
    },
    hasAction() {
      return ruleFields.some((f) => this.rule[f.key].action !== 'keep')
    },
    counts() {
      return this.preview?.page_match_counts || { ready: 0, skipped: 0, unknown: 0, failed: 0 }
    },
    previewCurrent() {
      try {
        return Boolean(this.preview && this.previewSignature === JSON.stringify(this.previewIntent()))
      } catch (_) {
        return false
      }
    },
    canCreate() {
      return (
        this.canQuery &&
        this.hasAction &&
        this.previewCurrent &&
        this.preview.total <= 10000 &&
        this.preview.selected_total > 0 &&
        !this.previewLoading &&
        !this.previewError &&
        !this.locked
      )
    },
    shownFields() {
      return fields
        .filter((f) => allowedFields.includes(f.key) && !Object.values(pairedFields).includes(f.key))
        .map((f) =>
          pairedFields[f.key]
            ? {
                ...f,
                matchKey: pairedFields[f.key],
                matchOptions: fields.find((x) => x.key === pairedFields[f.key]).options
              }
            : f
        )
    },
    selectorPages() {
      return Math.max(
        1,
        Math.ceil((this.selectorTab === 'selected' ? this.draftSelected.length : this.selectorTotal) / this.selectorPageSize)
      )
    },
    editorFields() {
      const labels = {
        maximum_per_order: ['每个订单最多购买', '每次限购不能超过累计限购；清空后按类目的系统推荐值执行。'],
        limit_per_buyer: ['每个用户累计可购买', '限制该商品所有SKU的累计购买总件数。'],
        minimum_per_order: ['每个订单至少购买', '清空后恢复1件起购，起购数不能超过有效的购买上限。']
      }
      return ['maximum_per_order', 'limit_per_buyer', 'minimum_per_order'].map((key) => ({
        ...ruleFields.find((f) => f.key === key),
        editorLabel: labels[key][0],
        tip: labels[key][1]
      }))
    },
    selectorProducts() {
      return this.selectorTab === 'selected'
        ? this.draftSelected.slice((this.selectorPage - 1) * this.selectorPageSize, this.selectorPage * this.selectorPageSize)
        : this.selectorRows
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    rule: {
      deep: true,
      handler() {
        this.ruleErrors = {}
        this.combinationError = ''
        this.invalidate()
      }
    },
    shops: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    selected: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    idsText() {
      this.invalidate()
    },
    idDraft() {
      this.invalidate()
    },
    scheduledAt() {
      this.refreshRequestId()
    },
    scheduled() {
      this.refreshRequestId()
    }
  },
  mounted() {
    this.loadOptions()
  },
  beforeDestroy() {
    this.previewRequest++
    this.selectorRequest++
  },
  methods: {
    changeSelectorPageSize(size) {
      this.selectorPageSize = Number(size)
      this.selectorPage = 1
      this.loadSelector(1)
    },
    refreshRequestId() {
      if (!this.locked) this.requestId = uuid()
    },
    invalidate() {
      this.previewRequest++
      this.previewLoading = false
      this.previewSignature = ''
      this.refreshRequestId()
    },
    closePreview() {
      this.invalidate()
      this.preview = null
      this.previewError = ''
    },
    dataOf(response) {
      if (!response || Number(response.code) !== 200) {
        const e = new Error(response?.message || '请求失败')
        e.businessCode = Number(response?.code)
        throw e
      }
      return response.data
    },
    message(error) {
      return error?.response?.data?.message || error?.message || '请求失败，请稍后重试'
    },
    async loadOptions() {
      this.optionsLoading = true
      this.optionsError = ''
      try {
        const [shops, categories] = await Promise.all([
          getAuthSuccessShopList(),
          getDoudianCategories({ shop_type: 1 })
        ])
        const shopData = this.dataOf(shops),
          cateData = this.dataOf(categories)
        this.shopOptions = Array.isArray(shopData) ? shopData : shopData.list || shopData.data || []
        const map = (rows) =>
          rows.map((x) => ({
            ...x,
            category_id: x.id || x.category_id,
            category_name: x.name || x.category_name,
            children: map(x.children || [])
          }))
        this.categories = map(Array.isArray(cateData) ? cateData : cateData.list || cateData.data || [])
      } catch (e) {
        this.optionsError = this.message(e)
      } finally {
        this.optionsLoading = false
      }
    },
    parseIds(text) {
      const ids = [
        ...new Set(
          String(text)
            .split(/[,，\s]+/)
            .map((x) => x.trim())
            .filter(Boolean)
        )
      ]
      const invalid = ids.filter((x) => !/^[1-9][0-9]{0,199}$/.test(x))
      if (invalid.length) throw new Error(`商品 ID 无效：${invalid[0]}，共${invalid.length}项`)
      return ids
    },
    productFilter() {
      if (this.mode === 'products')
        return {
          shop_ids: [...this.shops],
          platform_product_id: [...new Set(this.selected.map((x) => String(x.platform_product_id)))],
          excluded_product_ids: [...this.excluded]
        }
      if (this.mode === 'product_ids')
        return {
          shop_ids: [...this.shops],
          platform_product_id: this.parseIds(this.idsText),
          excluded_product_ids: [...this.excluded],
          ...(this.idDraft !== '' ? { is_draft: this.idDraft } : {})
        }
      const active = {}
      fields
        .filter((f) => allowedFields.includes(f.key))
        .forEach((f) => {
          if (f.range) {
            active[f.key + '_min'] = this.filters[f.key + '_min']
            active[f.key + '_max'] = this.filters[f.key + '_max']
          } else active[f.key] = this.filters[f.key]
        })
      if (!Object.values(active.category_id).some((v) => v.length)) delete active.category_id
      if (!Array.isArray(active.created_at) || active.created_at.length !== 2) delete active.created_at
      Object.entries(pairedFields).forEach(([key, modeKey]) => {
        if (
          !active[key] &&
          !(key === 'price' && (active.price_min !== '' || active.price_max !== '')) &&
          !(key === 'sku_code' && active[modeKey] === 'empty')
        )
          delete active[modeKey]
      })
      if (active.sku_code_mode === 'empty') delete active.sku_code
      return buildBatchProductFilter(active, this.shops, this.excluded, 'range')
    },
    previewIntent() {
      const rules = {}
      ruleFields.forEach(({ key }) => {
        const r = this.rule[key]
        rules[key] = { action: r.action, ...(r.action === 'set' ? { value: Number(String(r.value).trim()) } : {}) }
      })
      return { selection_mode: this.mode, product_filter: this.productFilter(), purchase_limit_rule: rules }
    },
    createPayload() {
      return {
        ...this.previewIntent(),
        request_id: this.requestId,
        ...(this.scheduled ? { scheduled_at: this.scheduledAt } : {})
      }
    },
    validateField(key) {
      const r = this.rule[key]
      const value = String(r.value).trim()
      const error =
        r.action === 'set' && (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value)))
          ? `${ruleFields.find((f) => f.key === key).label}请输入有效的正整数`
          : ''
      this.$set(this.ruleErrors, key, error)
      return !error
    },
    validate(requireAction = false, creating = false) {
      if (!this.canQuery) throw new Error(this.optionsError || '暂无可操作的授权抖店')
      if (this.mode === 'products' && !this.selected.length) throw new Error('请选择商品')
      if (this.mode === 'products' && this.selected.length > 10000) throw new Error('最多选择10000个商品')
      if (this.mode === 'product_ids') {
        const ids = this.parseIds(this.idsText)
        if (!ids.length || ids.length > 2000) throw new Error('请输入1至2000个去重后的商品 ID')
      }
      if (requireAction && !this.hasAction) throw new Error('请至少设置或清空一项限购规则')
      const checks = ruleFields.map((f) => this.validateField(f.key))
      if (checks.includes(false)) throw new Error('请检查限购数量输入')
      const [total, maximum, minimum] = ruleFields.map(({ key }) =>
        this.rule[key].action === 'set' ? Number(this.rule[key].value) : null
      )
      this.combinationError =
        total != null && maximum != null && maximum > total
          ? '每次限购件数不能超过累计限购件数'
          : minimum != null && ((maximum != null && minimum > maximum) || (total != null && minimum > total))
          ? '每次至少购买不能超过每次或累计限购件数'
          : ''
      if (this.combinationError) throw new Error(this.combinationError)
      const f = this.productFilter()
      for (const prefix of ['stock', 'price', 'sales']) {
        for (const suffix of ['min', 'max']) {
          const n = f[prefix + '_' + suffix]
          if (n != null && (!Number.isFinite(n) || n < 0 || (prefix !== 'price' && !Number.isInteger(n))))
            throw new Error('范围必须为有效的非负数')
        }
        if (f[prefix + '_min'] != null && f[prefix + '_max'] != null && f[prefix + '_min'] > f[prefix + '_max'])
          throw new Error('范围起点不能大于终点')
      }
      if (
        f.status !== undefined &&
        f.status !== '' &&
        f.is_draft !== undefined &&
        ((f.status === 6 && f.is_draft === 0) || (f.status !== 6 && f.is_draft === 1))
      )
        throw new Error('商品状态与草稿条件冲突')
      if (
        creating &&
        this.scheduled &&
        (!this.scheduledAt || new Date(this.scheduledAt.replace(' ', 'T') + '+08:00').getTime() <= Date.now())
      )
        throw new Error('请选择未来的执行时间')
    },
    async loadPreview(page = 1, requireAction = false) {
      if (this.locked) return
      try {
        this.validate(requireAction)
      } catch (e) {
        return this.$message.warning(this.message(e))
      }
      const intent = this.previewIntent(),
        signature = JSON.stringify(intent),
        request = ++this.previewRequest
      this.previewLoading = true
      this.previewError = ''
      try {
        const data = this.dataOf(await previewDoudianPurchaseLimitBatch({ ...intent, page, limit: 20 }))
        if (request !== this.previewRequest || signature !== JSON.stringify(this.previewIntent())) return
        this.preview = data
        this.previewSignature = signature
      } catch (e) {
        if (request === this.previewRequest) {
          this.previewError = this.message(e)
          this.previewSignature = ''
          this.$message.error(this.previewError)
        }
      } finally {
        if (request === this.previewRequest) this.previewLoading = false
      }
    },
    exclude(id, checked) {
      const n = Number(id)
      this.excluded = checked ? [...new Set([...this.excluded, n])] : this.excluded.filter((x) => x !== n)
      this.invalidate()
      this.loadPreview(this.preview.page, false)
    },
    resetFilters() {
      this.filters = defaults()
      this.idsText = ''
      this.idDraft = ''
      this.shops = []
      this.excluded = []
      this.invalidate()
    },
    switchMode(mode) {
      if (mode === this.mode || this.locked) return
      this.mode = mode
      this.selected = []
      this.idsText = ''
      this.filters = defaults()
      this.idDraft = ''
      this.shops = []
      this.excluded = []
      this.preview = null
      this.invalidate()
    },
    oldLabel(value, key) {
      if (value == null) return '待核验'
      if (value === 0) return key === 'maximum_per_order' ? '未设置（按类目推荐）' : '未设置（平台值0）'
      return `${value} 件`
    },
    targetLabel(row, key) {
      const action = row.target_actions?.[key]
      if (action === 'clear')
        return key === 'minimum_per_order'
          ? '清空（恢复1件起购）'
          : key === 'maximum_per_order'
          ? '清空（按类目推荐）'
          : '清空（按平台规则）'
      const v = action === 'keep' ? row.old_values?.[key] : row.target_values?.[key]
      return this.oldLabel(v, key) + (action === 'keep' ? '（不修改）' : '')
    },
    async createBatch() {
      if (!this.canCreate) return
      try {
        this.validate(true, true)
      } catch (e) {
        return this.$message.warning(this.message(e))
      }
      const frozen = this.createPayload(),
        signature = JSON.stringify(frozen)
      this.createSubmitting = true
      const descriptions = ruleFields.map(
        (f) =>
          `${f.label}：${
            this.rule[f.key].action === 'set'
              ? `设为${this.rule[f.key].value}件`
              : this.rule[f.key].action === 'clear'
              ? f.key === 'minimum_per_order'
                ? '恢复1件起购'
                : '清空'
              : '不修改'
          }`
      )
      try {
        await this.$confirm(
          `本次提交${this.preview.selected_total}个商品。${descriptions.join('；')}。${
            this.scheduled ? `北京时间${this.scheduledAt}执行` : '立即执行'
          }。实际命中以执行时抖店详情复核为准，可能跳过或失败。清空后遵循平台规则；修改可能进入平台审核。`,
          '确认批量修改商品限购',
          { confirmButtonText: '确认创建任务', cancelButtonText: '返回检查' }
        )
        if (signature !== JSON.stringify(this.createPayload())) throw new Error('条件已变化，请重新确认')
        this.validate(true, true)
        this.frozenCreatePayload = JSON.parse(signature)
      } catch (e) {
        this.createSubmitting = false
        if (!['cancel', 'close'].includes(e)) this.$message.warning(this.message(e))
        return
      }
      await this.submitFrozen()
    },
    async submitFrozen() {
      if (!this.frozenCreatePayload || (this.createSubmitting && this.createUncertain)) return
      this.createSubmitting = true
      try {
        const data = this.dataOf(await createDoudianPurchaseLimitBatch(this.frozenCreatePayload))
        if (!Number.isInteger(data?.id) || !data.batch_no || data.type !== 13)
          throw new Error('创建响应不完整，结果待确认')
        this.createUncertain = false
        this.$message.success('任务已创建，请查看批量修改记录')
        this.$emit('created')
      } catch (e) {
        const code = e.businessCode || Number(e?.response?.data?.code || e?.response?.status)
        this.createUncertain = ![401, 403, 422].includes(code)
        if (code === 403) this.previewSignature = ''
        this.$message.error(this.createUncertain ? '创建结果尚未确认，请确认创建结果，勿重复新建' : this.message(e))
      } finally {
        this.createSubmitting = false
      }
    },
    toggleSchedule(value) {
      this.scheduled = value
      if (value) this.openSchedule()
      else {
        this.scheduledAt = ''
        this.scheduleDraft = ''
      }
    },
    openSchedule() {
      this.scheduleDraft = this.scheduledAt
      this.scheduleVisible = true
    },
    scheduleClosed() {
      this.scheduleDraft = ''
      if (!this.scheduledAt) this.scheduled = false
    },
    confirmSchedule() {
      const date = new Date(String(this.scheduleDraft).replace(' ', 'T') + '+08:00')
      if (!this.scheduleDraft || !Number.isFinite(date.getTime()) || date.getTime() <= Date.now())
        return this.$message.warning('请选择未来的执行时间')
      this.scheduledAt = this.scheduleDraft
      this.scheduleVisible = false
    },
    async goBack() {
      if (this.locked) return this.$message.warning('创建结果尚未确认，请先确认创建结果')
      if (this.hasAction || this.selected.length || this.idsText || this.excluded.length || this.scheduled) {
        try {
          await this.$confirm('离开后将丢失当前设置，确定返回吗？', '返回批量修改', {
            confirmButtonText: '返回',
            cancelButtonText: '继续编辑'
          })
        } catch (_) {
          return
        }
      }
      this.$emit('back')
    },
    productId(row) {
      return Number(row.distribute_product_id || row.id)
    },
    productTitle(row) {
      return row.product_title || row.title || ''
    },
    productImage(row) {
      return row.main_image || row.img || ''
    },
    productPrice(row) {
      return row.product_price == null ? '价格待定' : '¥' + Number(row.product_price).toFixed(2)
    },
    isSelected(row) {
      return this.draftSelected.some((x) => this.productId(x) === this.productId(row))
    },
    openSelector() {
      this.draftSelected = JSON.parse(JSON.stringify(this.selected))
      this.selectorShopId = this.selected[0]?.shop_id || ''
      this.selectorTab = 'all'
      this.selectorVisible = true
      this.loadSelector(1)
    },
    toggleProduct(row) {
      if (
        !this.isSelected(row) &&
        this.draftSelected.length &&
        Number(this.draftSelected[0].shop_id) !== Number(row.shop_id)
      )
        return this.$message.warning('只能选择同一家店铺的商品')
      if (!this.isSelected(row) && this.draftSelected.length >= 10000)
        return this.$message.warning('最多选择10000个商品')
      this.draftSelected = this.isSelected(row)
        ? this.draftSelected.filter((x) => this.productId(x) !== this.productId(row))
        : [...this.draftSelected, row]
    },
    togglePage() {
      const all = this.selectorRows.every(this.isSelected)
      this.selectorRows.forEach((row) => {
        if (this.isSelected(row) === all) this.toggleProduct(row)
      })
    },
    changeSelectorTab(tab) {
      this.selectorTab = tab
      this.changeSelectorPage(1)
    },
    changeSelectorPage(page) {
      const p = Math.max(1, Math.min(this.selectorPages, Number(page) || 1))
      if (this.selectorTab === 'all') this.loadSelector(p)
      else {
        this.selectorPage = p
        this.selectorJump = p
      }
    },
    changeSelectorShop(id) {
      this.selectorShopId = id
      this.loadSelector(1)
    },
    confirmSelected() {
      if (this.draftSelected.some((x) => Number(x.shop_id) !== Number(this.draftSelected[0].shop_id)))
        return this.$message.warning('只能选择同一家店铺的商品')
      this.selected = [...this.draftSelected]
      this.shops = this.selected.length ? [Number(this.selected[0].shop_id)] : []
      this.excluded = []
      this.selectorVisible = false
      this.invalidate()
    },
    async loadSelector(page) {
      const request = ++this.selectorRequest
      this.selectorLoading = true
      const { created_at, ...filters } = this.selectorFilters
      try {
        const data = this.dataOf(
          await getProductSelectorProducts({
            page,
            page_size: this.selectorPageSize,
            ...filters,
            ...(created_at?.length === 2 ? { created_at } : {}),
            ...(this.selectorShopId ? { shop_ids: [this.selectorShopId] } : {})
          })
        )
        if (request !== this.selectorRequest) return
        this.selectorRows = data.list || []
        this.selectorTotal = Number(data.pagination?.total || data.total || 0)
        this.selectorPage = page
        this.selectorJump = page
      } catch (e) {
        if (request === this.selectorRequest) this.$message.error(this.message(e))
      } finally {
        if (request === this.selectorRequest) this.selectorLoading = false
      }
    }
  }
}
</script>

<style scoped src="./batch-modify.css"></style>
<style scoped>
.purchase-limit {
  min-width: 0;
}
.purchase-limit > main {
  padding-bottom: 126px;
}
.purchase-limit-editor {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin: 12px 0;
}
.editor-label {
  flex: 0 0 64px;
  padding-top: 12px;
  color: #606266;
  font-size: 12px;
}
.limit-rule-content {
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  background: #f8f8fa;
  border-radius: 4px;
}
.limit-rule-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px 20px;
}
.limit-rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.limit-rule-row > label {
  flex: 0 0 150px;
  color: #555;
  font-size: 12px;
}
.limit-rule-row > label .el-icon-question {
  margin-left: 6px;
  color: #909399;
  font-size: 12px;
}
.rule-action {
  flex: 0 0 86px;
  width: 86px;
}
.quantity {
  flex: 1 1 120px;
  min-width: 120px;
  max-width: 180px;
}
.quantity-unit {
  display: inline-flex;
  height: 28px;
  align-items: center;
  padding-right: 4px;
  color: #264568;
}
.quantity ::v-deep .el-input__inner {
  padding-right: 25px;
}
.quantity ::v-deep .el-input__inner,
.rule-action ::v-deep .el-input__inner {
  height: 28px;
  line-height: 28px;
  font-size: 12px;
}
.rule-action ::v-deep .el-input__icon {
  line-height: 28px;
}
.limit-rule-row .rule-error,
.limit-rule-row > .hint {
  flex-basis: 100%;
  margin: 0;
}
.limit-activity-hint {
  margin: 12px 0 0;
  color: #909399;
  font-size: 12px;
  line-height: 20px;
}
.rule-error,
.load-error {
  color: #c8443c;
  font-size: 12px;
}
.preview-warning {
  color: #b88230;
  font-size: 12px;
}
.limit-rules > .hint,
.limit-rules > .rule-error {
  margin: 10px 16px;
}
.id-area {
  display: grid;
  gap: 8px;
}
.draft-control {
  display: flex;
  align-items: center;
  gap: 8px;
}
.limit-preview .table-wrap {
  position: relative;
  overflow-x: auto;
}
.limit-preview-table {
  min-width: 1000px;
  width: 100%;
  table-layout: fixed;
}
.limit-preview-table th:first-child {
  width: 28%;
}
.limit-preview-table th:nth-child(2),
.limit-preview-table th:nth-child(3) {
  width: 20%;
}
.limit-preview-table th:last-child,
.limit-preview-table td:last-child {
  width: 110px;
  box-sizing: border-box;
  position: sticky;
  right: 0;
  background: #fff;
  box-shadow: -1px 0 0 #ececf2;
}
.limit-preview-table th:last-child {
  background: #fafafa;
}
.limit-preview-table ::v-deep .el-checkbox__label {
  padding-left: 6px;
  font-size: 12px;
}
.preview-product {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  text-align: left;
}
.preview-product-image {
  flex: 0 0 48px;
  height: 48px;
  width: 48px;
  border-radius: 4px;
}
.preview-product > div {
  min-width: 0;
}
.preview-product p {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 5px 0;
  overflow-wrap: anywhere;
}
.preview-product small,
.missing-ids {
  overflow-wrap: anywhere;
}
.value-line {
  line-height: 24px;
  text-align: left;
  font-size: 12px;
}
.not-executing {
  display: block;
  text-align: left;
  color: #909399;
}
.match-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
}
.result-ready {
  background: #f0f9eb;
  color: #529b2e;
}
.result-skipped {
  background: #f5f5f5;
  color: #909399;
}
.result-unknown {
  background: #fdf6ec;
  color: #b88230;
}
.result-failed {
  background: #fef0f0;
  color: #f56c6c;
}
.preview-reason {
  font-size: 12px;
  color: #777;
}
.preview-cover {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  background: #ffffffd9;
  color: #666;
}
.limit-actions {
  flex-wrap: wrap;
}
.limit-actions label {
  display: flex;
  align-items: center;
  gap: 6px;
}
.excluded-row {
  color: #909399;
}
@media (max-width: 520px) {
  .limit-actions .preview-button {
    display: inline-flex;
    align-items: center;
  }
  .limit-actions .start {
    margin: 0;
  }
  main {
    padding-bottom: 100px;
  }
  .purchase-limit-editor {
    flex-direction: column;
    gap: 8px;
  }
  .editor-label {
    flex-basis: auto;
    padding-top: 0;
  }
  .limit-rule-content {
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
  }
  .limit-rule-row > label {
    flex-basis: calc(100% - 20px);
  }
}
</style>
